import { call, put, select, takeEvery } from "redux-saga/effects";
import _cloneDeep from "lodash/cloneDeep";
import * as api from "../../../api/api";
import { updateVehicle } from "../../../action";
import { getCommonBenefits } from "../../../selector";
import { emitJourneyEvent, JOURNEY_STEP_TYPES } from "../../../utils/journeyMonitoring";
import { sanitizeVehiclePayload } from "../../../utils/sanitizeVehiclePayload";
import {
  SUBMIT_ODOMETER_PARTIAL_SAVE_REQUEST,
  submitOdometerPartialSaveSuccess,
  submitOdometerPartialSaveFailed,
} from "../actions";
import { OdometerPartialSaveRequestPayload } from "../types";

export function* submitOdometerPartialSave(action: {
  type: string;
  payload: OdometerPartialSaveRequestPayload;
}) {
  const { serviceRequestPayload, partnerCode, odometerReading, history, dispatch } = action.payload;
  let benefitsOdometerReading;
  try {
    const payloadWithMileage = _cloneDeep(serviceRequestPayload);
    payloadWithMileage.serviceRequest = {
      ...payloadWithMileage.serviceRequest,
      currentMileage: odometerReading,
    };

    // Include the vehicle details returned on the benefits response (e.g. from the Wheels VIN
    // lookup) in this partial save, mirroring the existing updateVehicle/sanitizeVehiclePayload
    // pattern used by DisablementLocation. Also dispatched to Redux so subsequent screens/saves
    // (Call OEM, Decline, and the eventual full rescue submission) retain the vehicle info.
    const commonBenefits = yield select(getCommonBenefits);
    const vehicle = commonBenefits?.vehicles?.[0];
    benefitsOdometerReading = vehicle?.lastKnownMileage;
    if (vehicle) {
      const sanitizedVehicle = sanitizeVehiclePayload(_cloneDeep(vehicle));
      payloadWithMileage.serviceRequest.vehicles = sanitizedVehicle;
      yield put(updateVehicle(sanitizedVehicle));
    }

    const response = yield call(api.saveRescueDetails, payloadWithMileage, partnerCode, "", history, dispatch);
    // saveRescueDetails resolves with err.response on HTTP errors, so check status rather than relying on a throw.
    const status = response?.status;
    if (status >= 200 && status < 300) {
      emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE, "odometer.submitted", {
        milestoneName: "odometer.submitted",
        partnerCode,
        userEnteredOdometerReading: odometerReading,
        benefitsOdometerReading,
      });
      yield put(submitOdometerPartialSaveSuccess());
    } else {
      emitJourneyEvent(JOURNEY_STEP_TYPES.API_FAILURE, "odometer.submit_failed", {
        milestoneName: "odometer.submit_failed",
        partnerCode,
        userEnteredOdometerReading: odometerReading,
        benefitsOdometerReading,
        status,
      });
      yield put(submitOdometerPartialSaveFailed(response));
    }
  } catch (error) {
    emitJourneyEvent(JOURNEY_STEP_TYPES.API_FAILURE, "odometer.submit_failed", {
      milestoneName: "odometer.submit_failed",
      partnerCode,
      userEnteredOdometerReading: odometerReading,
      benefitsOdometerReading,
    });
    yield put(submitOdometerPartialSaveFailed(error));
  }
}

export function* watchSubmitOdometerPartialSave() {
  yield takeEvery(SUBMIT_ODOMETER_PARTIAL_SAVE_REQUEST, submitOdometerPartialSave);
}
